import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBanner from '../layout/PageBanner';
import BlogSidebar from '../components/BlogSidebar';
import TouchStart from '../components/TouchStart';
import { BlogPost } from '../types/blog';
import blogService from '../services/blogService';

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8083';

const BlogList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const postsPerPage = 9;

    // Get filters from URL parameters
    useEffect(() => {
        const categoryParam = searchParams.get('category');
        const tagParam = searchParams.get('tag');
        setSelectedCategory(categoryParam);
        setSelectedTag(tagParam);
    }, [searchParams]);

    // Helper function to construct full image URL
    const getImageUrl = (imagePath: string | undefined): string => {
        if (!imagePath) return '';
        if (imagePath.startsWith('/storage/')) {
            return `${API_BASE_URL}${imagePath}`;
        }
        if (imagePath.startsWith('http')) {
            return imagePath;
        }
        return imagePath;
    };

    useEffect(() => {
        loadPosts();
    }, []);

    const loadPosts = async () => {
        try {
            setLoading(true);
            const response = await blogService.getPublished(1, 100);
            if (response.success && response.data) {
                const convertedPosts: BlogPost[] = response.data.map((post: any) => ({
                    id: post.public_id || `blog-${Date.now()}`,  // ✅ USE PUBLIC_ID
                    title: post.title,
                    slug: post.slug,
                    content: post.content,
                    excerpt: post.excerpt,
                    featuredImage: post.featured_image || '',
                    images: post.featured_image ? [post.featured_image] : [],
                    author: post.author,
                    publishDate: post.publish_date ? post.publish_date.split('T')[0] : new Date().toISOString().split('T')[0],
                    lastModified: post.updated_at,
                    status: post.status,
                    categories: post.category ? [post.category.name] : [],
                    tags: post.tags?.map((t: any) => t.name) || [],
                    seo: {
                        metaTitle: post.meta_title || post.title,
                        metaDescription: post.meta_description || post.excerpt,
                        metaKeywords: post.meta_keywords?.split(',').map((k: string) => k.trim()) || [],
                        ogTitle: post.og_title || post.title,
                        ogDescription: post.og_description || post.excerpt,
                        ogImage: post.og_image || post.featured_image,
                        canonicalUrl: post.canonical_url || `/blog/${post.slug}`,
                    },
                    views: post.views || 0,
                    commentsEnabled: post.comments_enabled ?? true,
                }));
                setPosts(convertedPosts.sort((a, b) =>
                    new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
                ));
            }
        } catch (error) {
            console.error('Error loading published posts:', error);
            // Fallback to localStorage if API fails
            const savedPosts = localStorage.getItem('blogPosts');
            if (savedPosts) {
                const allPosts = JSON.parse(savedPosts);
                const publishedPosts = allPosts.filter((post: BlogPost) => post.status === 'published');
                setPosts(publishedPosts.sort((a: BlogPost, b: BlogPost) =>
                    new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
                ));
            }
        } finally {
            setLoading(false);
        }
    };

    // Apply filters whenever posts or filters change
    useEffect(() => {
        let filtered = [...posts];

        if (selectedCategory) {
            filtered = filtered.filter(post => post.categories.includes(selectedCategory));
        }

        if (selectedTag) {
            filtered = filtered.filter(post => post.tags.includes(selectedTag));
        }

        setFilteredPosts(filtered);
        setCurrentPage(1); // Reset to first page when filter changes
    }, [posts, selectedCategory, selectedTag]);

    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
    const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const handlePostClick = (slug: string) => {
        navigate(`/blog-detail?slug=${slug}`);
    };

    const truncateText = (text: string, maxLength: number) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const stripHtmlTags = (html: string) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const handleCategoryFilter = (category: string) => {
        if (selectedCategory === category) {
            // Remove filter if clicking the same category
            setSearchParams({});
            setSelectedCategory(null);
        } else {
            setSearchParams({ category });
            setSelectedCategory(category);
        }
    };

    const handleTagFilter = (tag: string) => {
        if (selectedTag === tag) {
            // Remove filter if clicking the same tag
            setSearchParams({});
            setSelectedTag(null);
        } else {
            setSearchParams({ tag });
            setSelectedTag(tag);
        }
    };

    return (
        <>
            <PageBanner
                parentPage={t('blogListPage.parentPage')}
                activePage={t('blogListPage.activePage')}
                motherMenu={t('blogListPage.motherMenu')}
            />
            <section className="main-our-blog page-our-blog">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="blog-list">
                                {/* Active Filter Display */}
                                {(selectedCategory || selectedTag) && (
                                    <div className="mb-4">
                                        <div className="alert alert-info d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>Active Filter:</strong>
                                                {selectedCategory && <span className="ms-2 badge bg-primary">Category: {selectedCategory}</span>}
                                                {selectedTag && <span className="ms-2 badge bg-secondary">Tag: {selectedTag}</span>}
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => {
                                                    setSearchParams({});
                                                    setSelectedCategory(null);
                                                    setSelectedTag(null);
                                                }}
                                            >
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {loading ? (
                            <div className="row">
                                <div className="col-12">
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-3 text-muted">Loading blog posts...</p>
                                    </div>
                                </div>
                            </div>
                        ) : filteredPosts.length === 0 ? (
                            <div className="row">
                                <div className="col-12">
                                    <div className="text-center py-5">
                                        <div style={{ fontSize: '4rem' }} className="mb-3">{selectedCategory || selectedTag ? '🔍' : '📝'}</div>
                                        <h3 className="mb-3">{selectedCategory || selectedTag ? 'No posts found' : 'No blog posts available yet'}</h3>
                                        <p className="text-muted">
                                            {selectedCategory || selectedTag
                                                ? 'No posts match your current filter. Try a different category or tag.'
                                                : 'Check back soon for exciting content!'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="row">
                                    {currentPosts.map((post) => (
                                        <div className="col-lg-4" key={post.id}>
                                            <div className="blog-box">
                                                <div className="blog-img-box">
                                                    {post.featuredImage ? (
                                                        <div
                                                            className="blog-img back-img"
                                                            style={{
                                                                backgroundImage: `url(${getImageUrl(post.featuredImage)})`,
                                                                height: '250px',
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handlePostClick(post.slug)}
                                                        ></div>
                                                    ) : (
                                                        <div
                                                            className="blog-img back-img"
                                                            style={{
                                                                height: '250px',
                                                                backgroundColor: '#f0f0f0',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handlePostClick(post.slug)}
                                                        >
                                                            <i className="fa fa-image" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                        </div>
                                                    )}
                                                    <div className="blog-date">
                                                        <Link to="#"><i className="fa fa-calendar" />
                                                            {new Date(post.publishDate).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'long',
                                                                year: 'numeric'
                                                            })}
                                                        </Link>
                                                    </div>
                                                </div>
                                                <div className="blog-text">
                                                    {post.categories.length > 0 && (
                                                        <div className="mb-2">
                                                            {post.categories.slice(0, 2).map((category, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="badge bg-secondary me-1"
                                                                    style={{ fontSize: '11px' }}
                                                                >
                                                                    {category}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <h3 className="h3-title">
                                                        <Link
                                                            to="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handlePostClick(post.slug);
                                                            }}
                                                        >
                                                            {truncateText(post.title, 60)}
                                                        </Link>
                                                    </h3>
                                                    <p>{truncateText(post.excerpt, 100)}</p>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <Link
                                                            to="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handlePostClick(post.slug);
                                                            }}
                                                        >
                                                            READ MORE <i className="fa fa-angle-right" />
                                                        </Link>
                                                        <small className="text-muted">
                                                            <i className="fa fa-user me-1"></i>
                                                            {post.author}
                                                        </small>
                                                    </div>
                                                    {post.tags.length > 0 && (
                                                        <div className="mt-3">
                                                            {post.tags.slice(0, 3).map((tag, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="badge bg-light text-dark me-1"
                                                                    style={{ fontSize: '10px', fontWeight: 'normal' }}
                                                                >
                                                                    #{tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="blog-pagination">
                                                <Link
                                                    to="#"
                                                    className={`pagination-arrow ${currentPage === 1 ? 'disabled' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (currentPage > 1) paginate(currentPage - 1);
                                                    }}
                                                >
                                                    <i className="fa fa-angle-left" />
                                                </Link>
                                                <ul>
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                                                        <li
                                                            key={number}
                                                            className={currentPage === number ? 'active' : ''}
                                                            onClick={() => paginate(number)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            {number}
                                                        </li>
                                                    ))}
                                                </ul>
                                                <Link
                                                    to="#"
                                                    className={`pagination-arrow ${currentPage === totalPages ? 'disabled' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (currentPage < totalPages) paginate(currentPage + 1);
                                                    }}
                                                >
                                                    <i className="fa fa-angle-right" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                            </div>
                        </div>
                        <BlogSidebar
                            onCategoryFilter={handleCategoryFilter}
                            onTagFilter={handleTagFilter}
                            selectedCategory={selectedCategory}
                            selectedTag={selectedTag}
                        />
                    </div>
                </div>
            </section>
            <div style={{ width: '80%', margin: '0 auto 60px auto' }}>
                <TouchStart />
            </div>
        </>
    );
};

export default BlogList;
